import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicator from '@mui/icons-material/DragIndicator';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import {
  SettlementExportLineDescriptionField,
  SettlementExportSide,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  LoadingSpinner,
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import StatusTabs from '~components/Tabs/StatusTabs';
import { RutterConnection } from '~hooks/useRutter/models';
import useSettlementExportLineDescriptionFormats from '~hooks/useSettlementExportLineDescriptionFormats/useSettlementExportLineDescriptionFormats';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';

const I18N_PREFIX =
  'administration.integration.rutter.quick_books_online.advanced_configuration.line_description';

const fieldIsOffered = {
  [SettlementExportLineDescriptionField.DATE]: true,
  [SettlementExportLineDescriptionField.JOB_ID]: true,
  [SettlementExportLineDescriptionField.TICKET]: true,
  [SettlementExportLineDescriptionField.JOB_TICKETS]: true,
  [SettlementExportLineDescriptionField.TRUCK]: true,
  [SettlementExportLineDescriptionField.MATERIAL]: true,
  [SettlementExportLineDescriptionField.ORDER_PO]: true,
  [SettlementExportLineDescriptionField.PROJECT]: true,
  [SettlementExportLineDescriptionField.HAUL]: true,
  [SettlementExportLineDescriptionField.DRIVER]: true,
  [SettlementExportLineDescriptionField.QUANTITY]: true,
  [SettlementExportLineDescriptionField.PAY_TIME]: true,
} satisfies Record<SettlementExportLineDescriptionField, boolean>;
// job tickets share the ticket row, as its "by job" option
const ROW_FIELDS: SettlementExportLineDescriptionField[] = Object.values(
  SettlementExportLineDescriptionField,
).filter(
  (field) =>
    fieldIsOffered[field] && field !== SettlementExportLineDescriptionField.JOB_TICKETS,
);
// matches the description the API exports when a side has no saved format
const DEFAULT_FIELDS = [
  SettlementExportLineDescriptionField.DATE,
  SettlementExportLineDescriptionField.JOB_ID,
  SettlementExportLineDescriptionField.TICKET,
  SettlementExportLineDescriptionField.JOB_TICKETS,
  SettlementExportLineDescriptionField.TRUCK,
  SettlementExportLineDescriptionField.MATERIAL,
];

const SIDES = [SettlementExportSide.RECEIVABLE, SettlementExportSide.PAYABLE];
const CHEVRON_TRANSITION = 'transform 150ms';
// one step above the 10px caption it sits under, so it stays readable as a button
const ADVANCED_TOGGLE_FONT_SIZE = 12;
// how long a toggled row takes to slide from its old spot to its new one
const REGROUP_SLIDE_MS = 200;

const moveToCheckedBoundary = (
  rows: FieldRow[],
  field: SettlementExportLineDescriptionField,
) => {
  const movedRow = rows.find((row) => row.field === field);
  if (!movedRow) return rows;

  const otherRows = rows.filter((row) => row.field !== field);
  const checkedCount = otherRows.filter((row) => row.isEnabled).length;

  return [
    ...otherRows.slice(0, checkedCount),
    movedRow,
    ...otherRows.slice(checkedCount),
  ];
};

type Itemization = 'job' | 'load';
type TicketScopes = Record<Itemization, boolean>;

const ITEMIZATIONS: Itemization[] = ['load', 'job'];
const ALL_TICKET_SCOPES: TicketScopes = { job: true, load: true };
const ITEMIZATION_LABEL_KEYS: Record<Itemization, string> = {
  job: `${I18N_PREFIX}.by_job`,
  load: `${I18N_PREFIX}.by_load`,
};
const ITEMIZATION_ONLY_KEYS: Record<Itemization, string> = {
  job: `${I18N_PREFIX}.by_job_only`,
  load: `${I18N_PREFIX}.by_load_only`,
};
const NO_FIELDS_IN_ITEMIZATION_KEYS: Record<Itemization, string> = {
  job: `${I18N_PREFIX}.no_fields_in_job_itemization`,
  load: `${I18N_PREFIX}.no_fields_in_load_itemization`,
};

// fields the API only renders for one itemization; the rest render for both
const fieldItemization: Partial<
  Record<SettlementExportLineDescriptionField, Itemization>
> = {
  [SettlementExportLineDescriptionField.TICKET]: 'load',
  [SettlementExportLineDescriptionField.JOB_TICKETS]: 'job',
  [SettlementExportLineDescriptionField.PAY_TIME]: 'job',
};

const fieldItemizationSummary = (field: SettlementExportLineDescriptionField) => {
  const itemization = fieldItemization[field];

  return itemization ? t(ITEMIZATION_ONLY_KEYS[itemization]) : null;
};

interface FieldRow {
  field: SettlementExportLineDescriptionField;
  isEnabled: boolean;
  ticketScopes?: TicketScopes;
}

const isTicketField = (field: SettlementExportLineDescriptionField) =>
  field === SettlementExportLineDescriptionField.TICKET ||
  field === SettlementExportLineDescriptionField.JOB_TICKETS;

const rowsFromFields = (fields: SettlementExportLineDescriptionField[]): FieldRow[] => {
  const ticketScopes = {
    job: fields.includes(SettlementExportLineDescriptionField.JOB_TICKETS),
    load: fields.includes(SettlementExportLineDescriptionField.TICKET),
  };
  const hasTicket = ticketScopes.job || ticketScopes.load;
  // the ticket row sits where the first of its two fields was saved
  const enabledRowFields = fields
    .map((field): SettlementExportLineDescriptionField =>
      isTicketField(field) ? SettlementExportLineDescriptionField.TICKET : field,
    )
    .filter((field, index, all) => all.indexOf(field) === index);

  return [
    ...enabledRowFields.map((field) => ({ field, isEnabled: true })),
    ...ROW_FIELDS.filter((field) => !enabledRowFields.includes(field)).map((field) => ({
      field,
      isEnabled: false,
    })),
  ].map((row) =>
    row.field === SettlementExportLineDescriptionField.TICKET
      ? { ...row, ticketScopes: hasTicket ? ticketScopes : ALL_TICKET_SCOPES }
      : row,
  );
};

const enabledFieldsOf = (rows: FieldRow[]) =>
  rows
    .filter((row) => row.isEnabled)
    .flatMap((row) =>
      row.ticketScopes
        ? [
            ...(row.ticketScopes.load
              ? [SettlementExportLineDescriptionField.TICKET]
              : []),
            ...(row.ticketScopes.job
              ? [SettlementExportLineDescriptionField.JOB_TICKETS]
              : []),
          ]
        : [row.field],
    );

// null when the ticket prints for both itemizations, which is the default
const ticketScopeSummary = (ticketScopes: TicketScopes | undefined) => {
  const onlyItemization = ITEMIZATIONS.find((itemization) => {
    const enabledItemizations = ITEMIZATIONS.filter((other) => ticketScopes?.[other]);

    return enabledItemizations.length === 1 && enabledItemizations[0] === itemization;
  });

  return onlyItemization ? t(ITEMIZATION_ONLY_KEYS[onlyItemization]) : null;
};

const buildPreview = (
  fields: SettlementExportLineDescriptionField[],
  itemization: Itemization,
) =>
  fields
    .filter((field) => (fieldItemization[field] ?? itemization) === itemization)
    .map((field) => t(`${I18N_PREFIX}.fields.${field}.sample`))
    .join(', ');

const FieldPosition = ({ position }: { position: number | null }) => (
  <Typography
    aria-hidden
    sx={{ color: 'text.secondary', flexShrink: 0, textAlign: 'right', width: 24 }}
    variant="body1"
  >
    {position === null ? '' : `${position}.`}
  </Typography>
);

interface SortableFieldRowProps {
  onTicketScopeToggle: (itemization: Itemization) => void;
  onToggle: (field: SettlementExportLineDescriptionField) => void;
  position: number | null;
  registerRowElement: (
    field: SettlementExportLineDescriptionField,
    element: HTMLElement | null,
  ) => void;
  row: FieldRow;
}

const SortableFieldRow = ({
  onTicketScopeToggle,
  onToggle,
  position,
  registerRowElement,
  row,
}: SortableFieldRowProps) => {
  const theme = useTheme();
  const { attributes, isDragging, listeners, setNodeRef, transform, transition } =
    useSortable({ id: row.field });
  const setRowRef = useCallback(
    (element: HTMLElement | null) => {
      setNodeRef(element);
      registerRowElement(row.field, element);
    },
    [registerRowElement, row.field, setNodeRef],
  );
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const label = t(`${I18N_PREFIX}.fields.${row.field}.label`);
  const { ticketScopes } = row;
  const itemizationCaption = ticketScopes
    ? ticketScopeSummary(ticketScopes)
    : fieldItemizationSummary(row.field);

  return (
    <Box
      ref={setRowRef}
      data-test-id={`line-description-field-${row.field}`}
      sx={{
        alignItems: 'center',
        bgcolor: isDragging
          ? theme.palette.action.selected
          : theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        gap: 1,
        minHeight: 56,
        opacity: row.isEnabled ? 1 : 0.7,
        px: 1,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <FieldPosition position={position} />
      <IconButton
        {...attributes}
        {...listeners}
        aria-label={t(`${I18N_PREFIX}.reorder`, { field: label })}
        size="small"
        sx={{ cursor: 'grab' }}
      >
        <DragIndicator fontSize="small" />
      </IconButton>
      <Checkbox
        disableRipple
        checked={row.isEnabled}
        id={`line-description-field-${row.field}-checkbox`}
        onChange={() => onToggle(row.field)}
        size="small"
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, py: 1 }}>
        <Box
          component="label"
          htmlFor={`line-description-field-${row.field}-checkbox`}
          sx={{ cursor: 'pointer', display: 'flex', flexDirection: 'column' }}
        >
          <Typography variant="body1">{label}</Typography>
          <Typography sx={{ color: 'text.secondary' }} variant="caption">
            {t(`${I18N_PREFIX}.fields.${row.field}.template`)}
            {itemizationCaption && ` · ${itemizationCaption}`}
          </Typography>
        </Box>
        {row.isEnabled && ticketScopes && (
          <>
            <Button
              aria-expanded={isAdvancedOpen}
              color="secondary"
              endIcon={
                <KeyboardArrowDown
                  sx={{
                    transform: isAdvancedOpen ? 'rotate(180deg)' : 'none',
                    transition: CHEVRON_TRANSITION,
                  }}
                />
              }
              onClick={() => setIsAdvancedOpen((isOpen) => !isOpen)}
              size="small"
              sx={{
                alignSelf: 'flex-start',
                mt: 0.5,
                px: 0,
                textTransform: 'none',
                fontSize: ADVANCED_TOGGLE_FONT_SIZE,
                height: 'auto',
              }}
              variant="text"
            >
              {t(`${I18N_PREFIX}.advanced`)}
            </Button>
            <Collapse in={isAdvancedOpen} unmountOnExit>
              <Box
                data-test-id="line-description-ticket-scopes"
                sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 2 }}
              >
                <Typography sx={{ color: 'text.secondary' }} variant="body2">
                  {t(`${I18N_PREFIX}.show_when_itemizing`)}
                </Typography>
                {ITEMIZATIONS.map((itemization) => (
                  <FormControlLabel
                    key={itemization}
                    control={
                      <Checkbox
                        disableRipple
                        checked={ticketScopes[itemization]}
                        onChange={() => onTicketScopeToggle(itemization)}
                        size="small"
                      />
                    }
                    label={t(ITEMIZATION_LABEL_KEYS[itemization])}
                    slotProps={{ typography: { variant: 'body2' } }}
                    sx={{ m: 0 }}
                  />
                ))}
              </Box>
            </Collapse>
          </>
        )}
      </Box>
    </Box>
  );
};

interface LineDescriptionFormatSectionProps {
  companyId: string;
  // read-only formats shown instead of fetching the company's own
  ownerFormats?: RutterConnection['ownerExportSettings']['lineDescriptionFormats'];
}

const LineDescriptionFormatSection = ({
  companyId,
  ownerFormats,
}: LineDescriptionFormatSectionProps) => {
  const readOnly = ownerFormats !== undefined;
  const theme = useTheme();
  const { toasterStore } = useStores();
  const {
    getLineDescriptionFormats,
    resetLineDescriptionFormat,
    saveLineDescriptionFormat,
  } = useSettlementExportLineDescriptionFormats();
  const [side, setSide] = useState<SettlementExportSide>(SettlementExportSide.RECEIVABLE);
  const [itemization, setItemization] = useState<Itemization>('load');
  const [savedFieldsBySide, setSavedFieldsBySide] = useState<Partial<
    Record<SettlementExportSide, SettlementExportLineDescriptionField[]>
  > | null>(null);
  const [rowsBySide, setRowsBySide] = useState<Record<SettlementExportSide, FieldRow[]>>({
    [SettlementExportSide.PAYABLE]: rowsFromFields(DEFAULT_FIELDS),
    [SettlementExportSide.RECEIVABLE]: rowsFromFields(DEFAULT_FIELDS),
  });
  const [didFetchFail, setDidFetchFail] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 3 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    let isCurrentCompany = true;

    const showFormats = (
      formats: Array<{
        fields: SettlementExportLineDescriptionField[];
        side: SettlementExportSide;
      }>,
    ) => {
      const savedFields: Partial<
        Record<SettlementExportSide, SettlementExportLineDescriptionField[]>
      > = {};
      formats.forEach((format) => {
        savedFields[format.side] = format.fields;
      });
      setSavedFieldsBySide(savedFields);
      setRowsBySide({
        [SettlementExportSide.PAYABLE]: rowsFromFields(
          savedFields[SettlementExportSide.PAYABLE] ?? DEFAULT_FIELDS,
        ),
        [SettlementExportSide.RECEIVABLE]: rowsFromFields(
          savedFields[SettlementExportSide.RECEIVABLE] ?? DEFAULT_FIELDS,
        ),
      });
    };

    if (ownerFormats) {
      showFormats(ownerFormats);

      return;
    }

    setSavedFieldsBySide(null);
    setDidFetchFail(false);

    const fetchFormats = async () => {
      try {
        const formats = await getLineDescriptionFormats(companyId);
        if (!isCurrentCompany || !formats) {
          return;
        }

        showFormats(formats);
      } catch {
        // getLineDescriptionFormats already toasted via handleRequestError
        if (isCurrentCompany) {
          setDidFetchFail(true);
        }
      }
    };

    void fetchFormats();

    return () => {
      isCurrentCompany = false;
    };
  }, [companyId, getLineDescriptionFormats, ownerFormats]);

  const rows = rowsBySide[side];
  const enabledFields = useMemo(() => enabledFieldsOf(rows), [rows]);
  const enabledRows = useMemo(() => rows.filter((row) => row.isEnabled), [rows]);
  const savedFields = savedFieldsBySide?.[side] ?? DEFAULT_FIELDS;
  const savedRows = useMemo(
    () =>
      rowsFromFields(savedFields)
        .filter((row) => row.isEnabled)
        .map((row) => ({ ...row, scopeSummary: ticketScopeSummary(row.ticketScopes) })),
    [savedFields],
  );
  const dirtySides = SIDES.filter(
    (eachSide) =>
      enabledFieldsOf(rowsBySide[eachSide]).join() !==
      enabledFieldsOf(
        rowsFromFields(savedFieldsBySide?.[eachSide] ?? DEFAULT_FIELDS),
      ).join(),
  );
  const isDirty = dirtySides.length > 0;
  const hasSideWithNoEnabledFields = SIDES.some(
    (eachSide) => enabledFieldsOf(rowsBySide[eachSide]).length === 0,
  );
  const previewFields = isEditing ? enabledFields : savedFields;
  const previewText = buildPreview(previewFields, itemization);
  const emptyPreviewMessage =
    previewFields.length === 0
      ? t(`${I18N_PREFIX}.select_a_field`)
      : t(NO_FIELDS_IN_ITEMIZATION_KEYS[itemization]);

  const setRows = useCallback(
    (updateRows: (current: FieldRow[]) => FieldRow[]) => {
      setRowsBySide((current) => ({ ...current, [side]: updateRows(current[side]) }));
    },
    [side],
  );

  const rowElementsRef = useRef(
    new Map<SettlementExportLineDescriptionField, HTMLElement>(),
  );
  const previousRowTopsRef = useRef(
    new Map<SettlementExportLineDescriptionField, number>(),
  );
  const shouldSlideRegroupedRowsRef = useRef(false);

  const registerRowElement = useCallback(
    (field: SettlementExportLineDescriptionField, element: HTMLElement | null) => {
      if (element) {
        rowElementsRef.current.set(field, element);
      } else {
        rowElementsRef.current.delete(field);
      }
    },
    [],
  );

  // slides rows from where they were drawn before a toggle regrouped them to where they are now;
  // drags are left to the drag library, which animates its own drop
  useLayoutEffect(() => {
    const currentRowTops = new Map<SettlementExportLineDescriptionField, number>();
    rowElementsRef.current.forEach((element, field) => {
      currentRowTops.set(field, element.getBoundingClientRect().top);
    });

    if (shouldSlideRegroupedRowsRef.current) {
      shouldSlideRegroupedRowsRef.current = false;
      rowElementsRef.current.forEach((element, field) => {
        const previousTop = previousRowTopsRef.current.get(field);
        const currentTop = currentRowTops.get(field);
        const distanceMoved =
          previousTop === undefined || currentTop === undefined
            ? 0
            : previousTop - currentTop;
        const canAnimate = typeof element.animate === 'function';
        if (distanceMoved !== 0 && canAnimate) {
          element.animate(
            [
              { transform: `translateY(${distanceMoved}px)` },
              { transform: 'translateY(0)' },
            ],
            { duration: REGROUP_SLIDE_MS, easing: 'ease-out' },
          );
        }
      });
    }

    previousRowTopsRef.current = currentRowTops;
  }, [rowsBySide, side, isEditing]);

  // the toggled row moves to the boundary between checked and unchecked rows, so checked
  // fields stay together and a newly checked one takes the next number
  const handleToggle = useCallback(
    (field: SettlementExportLineDescriptionField) => {
      shouldSlideRegroupedRowsRef.current = true;
      setRows((current) =>
        moveToCheckedBoundary(
          current.map((row) => {
            if (row.field !== field) return row;

            const isEnabling = !row.isEnabled;
            const isReEnablingWithNoScopes =
              isEnabling &&
              row.ticketScopes &&
              !row.ticketScopes.load &&
              !row.ticketScopes.job;
            return isReEnablingWithNoScopes
              ? { ...row, isEnabled: true, ticketScopes: ALL_TICKET_SCOPES }
              : { ...row, isEnabled: isEnabling };
          }),
          field,
        ),
      );
    },
    [setRows],
  );

  const handleTicketScopeToggle = useCallback(
    (itemization: Itemization) => {
      setRows((current) => {
        const ticketRow = current.find(
          (row) => row.field === SettlementExportLineDescriptionField.TICKET,
        );
        if (!ticketRow?.ticketScopes) return current;

        const ticketScopes = {
          ...ticketRow.ticketScopes,
          [itemization]: !ticketRow.ticketScopes[itemization],
        };
        const isStillEnabled = ticketScopes.load || ticketScopes.job;
        const updatedRows = current.map((row) =>
          row === ticketRow ? { ...row, isEnabled: isStillEnabled, ticketScopes } : row,
        );
        if (isStillEnabled) return updatedRows;

        shouldSlideRegroupedRowsRef.current = true;
        return moveToCheckedBoundary(updatedRows, ticketRow.field);
      });
    },
    [setRows],
  );

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (!over || active.id === over.id) return;

      setRows((current) => {
        const oldIndex = current.findIndex((row) => row.field === active.id);
        const newIndex = current.findIndex((row) => row.field === over.id);

        const movedRows = arrayMove(current, oldIndex, newIndex);

        return [
          ...movedRows.filter((row) => row.isEnabled),
          ...movedRows.filter((row) => !row.isEnabled),
        ];
      });
    },
    [setRows],
  );

  const handleResetToDefault = useCallback(() => {
    setRows(() => rowsFromFields(DEFAULT_FIELDS));
  }, [setRows]);

  const resetRowsToSaved = useCallback(() => {
    setRowsBySide({
      [SettlementExportSide.PAYABLE]: rowsFromFields(
        savedFieldsBySide?.[SettlementExportSide.PAYABLE] ?? DEFAULT_FIELDS,
      ),
      [SettlementExportSide.RECEIVABLE]: rowsFromFields(
        savedFieldsBySide?.[SettlementExportSide.RECEIVABLE] ?? DEFAULT_FIELDS,
      ),
    });
  }, [savedFieldsBySide]);

  const enterEditMode = useCallback(() => {
    resetRowsToSaved();
    setIsEditing(true);
  }, [resetRowsToSaved]);

  const exitEditMode = useCallback(() => {
    resetRowsToSaved();
    setIsEditing(false);
  }, [resetRowsToSaved]);

  // saves each changed side in turn; a side that saved stays saved if a later one fails
  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      for (const dirtySide of dirtySides) {
        const fields = enabledFieldsOf(rowsBySide[dirtySide]);
        const isDefaultFormat = fields.join() === DEFAULT_FIELDS.join();
        if (isDefaultFormat) {
          if (savedFieldsBySide?.[dirtySide]) {
            await resetLineDescriptionFormat({ companyId, side: dirtySide });
          }
          setSavedFieldsBySide((current) => ({ ...current, [dirtySide]: undefined }));
        } else {
          const format = await saveLineDescriptionFormat({
            companyId,
            fields,
            side: dirtySide,
          });
          setSavedFieldsBySide((current) => ({
            ...current,
            [dirtySide]: format?.fields,
          }));
        }
      }
      setIsEditing(false);
      toasterStore.push(alert(t(`${I18N_PREFIX}.saved`), AlertTypes.success));
    } catch {
      // the hook already toasted via handleRequestError
    } finally {
      setIsSaving(false);
    }
  }, [
    companyId,
    dirtySides,
    resetLineDescriptionFormat,
    rowsBySide,
    saveLineDescriptionFormat,
    savedFieldsBySide,
    toasterStore,
  ]);

  const sideTabs = useMemo(
    () => [
      {
        label: t(`${I18N_PREFIX}.tabs.receivable`),
        value: SettlementExportSide.RECEIVABLE,
      },
      { label: t(`${I18N_PREFIX}.tabs.payable`), value: SettlementExportSide.PAYABLE },
    ],
    [],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 'normal' }}>
          {t(`${I18N_PREFIX}.title`)}
        </Typography>
        <Typography sx={{ color: theme.palette.text.secondary }} variant="body2">
          {t(`${I18N_PREFIX}.description`)}
        </Typography>
      </Box>

      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: 2,
          justifyContent: 'space-between',
          minHeight: 40,
        }}
      >
        <StatusTabs onChange={setSide} selectedTab={side} tabs={sideTabs} />
        {savedFieldsBySide && !readOnly && (
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            {isEditing ? (
              <>
                <SmallButton
                  color="secondary"
                  disabled={isSaving}
                  onClick={handleResetToDefault}
                  variant="text"
                >
                  {t(`${I18N_PREFIX}.reset_to_default`)}
                </SmallButton>
                <SmallButton
                  color="secondary"
                  disabled={isSaving}
                  onClick={exitEditMode}
                  variant="outlined"
                >
                  {t('actions.cancel')}
                </SmallButton>
                <SmallLoadingButton
                  color="brandV2Yellow"
                  disabled={!isDirty || hasSideWithNoEnabledFields}
                  loading={isSaving}
                  onClick={handleSave}
                >
                  {t('actions.save')}
                </SmallLoadingButton>
              </>
            ) : (
              <SmallButton color="brandV2Yellow" onClick={enterEditMode}>
                {t('actions.edit')}
              </SmallButton>
            )}
          </Stack>
        )}
      </Box>

      {didFetchFail && (
        <Alert severity="error" variant="outlined">
          {t('error_messages.settlement_export_line_description_formats.failed_to_fetch')}
        </Alert>
      )}
      {!didFetchFail && !savedFieldsBySide && (
        <LoadingSpinner isVisible={true} loadingIndicatorSize={16} />
      )}
      {savedFieldsBySide && (
        <Box
          sx={{
            alignItems: 'start',
            display: 'grid',
            gap: 3,
            gridTemplateColumns: {
              md: 'repeat(2, minmax(0, 1fr))',
              xs: 'minmax(0, 1fr)',
            },
          }}
        >
          {isEditing ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <DndContext
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                  sensors={sensors}
                >
                  <SortableContext
                    items={rows.map((row) => row.field)}
                    strategy={verticalListSortingStrategy}
                  >
                    {rows.map((row) => (
                      <SortableFieldRow
                        key={row.field}
                        onTicketScopeToggle={handleTicketScopeToggle}
                        onToggle={handleToggle}
                        registerRowElement={registerRowElement}
                        position={
                          row.isEnabled
                            ? enabledRows.findIndex(
                                (enabled) => enabled.field === row.field,
                              ) + 1
                            : null
                        }
                        row={row}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </Box>
            </Box>
          ) : (
            <Box data-test-id="line-description-saved-fields">
              <Typography sx={{ fontWeight: 600 }} variant="subtitle1">
                {t(`${I18N_PREFIX}.fields_in_order`)}
              </Typography>
              <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                {savedRows.map((row, index) => (
                  <Box
                    component="li"
                    key={row.field}
                    sx={{ alignItems: 'baseline', display: 'flex', gap: 1, py: 0.5 }}
                  >
                    <FieldPosition position={index + 1} />
                    <Typography variant="body1">
                      {t(`${I18N_PREFIX}.fields.${row.field}.label`)}
                      {row.scopeSummary && (
                        <Typography
                          component="span"
                          sx={{ color: 'text.secondary' }}
                          variant="body2"
                        >
                          {` · ${row.scopeSummary}`}
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          <Box
            data-test-id="line-description-preview-panel"
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              p: 2,
            }}
          >
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Typography sx={{ fontWeight: 600 }} variant="subtitle1">
                {t(`${I18N_PREFIX}.preview_title.${side}`)}
              </Typography>
              <ToggleButtonGroup
                exclusive
                onChange={(_event, value: Itemization | null) =>
                  value && setItemization(value)
                }
                size="small"
                sx={{ '& .MuiToggleButton-root': { textTransform: 'none' } }}
                value={itemization}
              >
                <ToggleButton value="load">
                  {t(`${I18N_PREFIX}.itemize_by_load`)}
                </ToggleButton>
                <ToggleButton value="job">
                  {t(`${I18N_PREFIX}.itemize_by_job`)}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Typography
              data-test-id="line-description-preview"
              sx={{
                bgcolor: theme.palette.action.hover,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                p: 1.5,
                wordBreak: 'break-word',
              }}
              variant="body2"
            >
              {previewText || emptyPreviewMessage}
            </Typography>
            <Typography sx={{ color: theme.palette.text.secondary }} variant="caption">
              {t(`${I18N_PREFIX}.preview_caption`)}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default LineDescriptionFormatSection;
